<script setup lang="ts">
import { standardKeymap } from "@codemirror/commands";
import { EditorSelection, EditorState } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { noop } from "lodash-es";
import { onMounted, ref, useTemplateRef } from "vue";

import { cardActions } from "@/action/cardActions";
import { stickyNoteTextChanged } from "@/analytics/events/sticky";
import { trackEvent } from "@/analytics/track";
import { cardKey } from "@/components/StickyNote/injectKeys";
import { accept } from "@/utils/shortcut/key";
import { hardBreaksAllowed } from "@/utils/text/sticky";
import { injectStrict } from "@/utils/vue";

import { useCopyPaste } from "./useCopyPaste";
import { useTextBreak } from "./useTextBreak";

const props = defineProps<{ fontSize: number }>();
const emit = defineEmits<{ deactivateNote: []; blur: [] }>();

const card = injectStrict(cardKey);

const editorRef = useTemplateRef("editor");
const editorView = ref<EditorView>();
const fontSize = ref(props.fontSize);

defineExpose({ focus: () => editorView.value?.focus(), element: () => editorRef.value });

const { breakState, breakText, rebreakText, newline } = useTextBreak(card, editorRef, fontSize);
const { copy, paste, maxLength } = useCopyPaste(hardBreaksAllowed(card.type));

onMounted(() => {
  editorView.value = createView(createState(), editorRef.value!);
});

function createState() {
  return EditorState.create({
    doc: "",
    extensions: [
      keymap.of([
        { key: "Alt-Enter", run: newline },
        { key: "Shift-Enter", run: newline },
        { key: "Enter", run: exit },
        { key: "Escape", run: exit },
        { any: duplicate, preventDefault: true },
      ]),
      keymap.of(standardKeymap),
      EditorView.contentAttributes.of({ spellcheck: "true" }),
      EditorView.updateListener.of(rebreakText),
      EditorView.mouseSelectionStyle.of(selectAllOnTripleClick),
      EditorState.changeFilter.of(maxLength),
      EditorView.domEventHandlers({ copy, paste }),
      breakState,
    ],
  });
}

function createView(state: EditorState, parent: HTMLElement) {
  const view = new EditorView({ state, parent });
  const dom = view.contentDOM;
  dom.addEventListener("blur", () => emit("blur"));
  dom.addEventListener(
    "input",
    () => trackEvent(stickyNoteTextChanged(card.type.functionality, card.text.length)),
    { once: true },
  );
  dom.addEventListener("keydown", (e) => e.stopPropagation());
  // cannot use editorView.domEventHandlers because it does some event processing/filtering
  // it does not catch e.g. the key after a dead key (^~`)
  dom.addEventListener("pointerdown", (e) => e.stopPropagation());
  view.dispatch(breakText(state, { anchor: 0 }, card.text));
  view.dispatch({ selection: { anchor: view.state.doc.length } });
  view.focus();
  return view;
}

function exit(view: EditorView) {
  emit("deactivateNote");
  view.contentDOM.blur();
  return true;
}

function duplicate(_: EditorView, event: KeyboardEvent) {
  if (accept(cardActions.duplicate.data.shortcut.accept)(event)) {
    void cardActions.duplicate("keyboard-shortcut");
    return true;
  }
  return false;
}

function selectAllOnTripleClick(view: EditorView, event: MouseEvent) {
  if (event.detail === 3 && event.button === 0) {
    return {
      get() {
        return EditorSelection.create([EditorSelection.range(0, view.state.doc.length)]);
      },
      update: noop,
    };
  }
  return null;
}
</script>

<template>
  <div ref="editor" class="sticky-note-editor" :style="{ fontSize: fontSize + '%' }" />
</template>

<style lang="scss">
@use "@/styles/variables";
@use "@/styles/mixins/typography";

.sticky-note-editor {
  width: 100%;
  height: 100%;
  cursor: text;

  @include typography.medium("regular");

  .cm-editor {
    // with 100%, lines sometimes move up a little bit when the last(?) line is edited for the first time
    // not idea why
    height: 105%;

    &.cm-focused {
      outline: none;
    }

    .cm-content {
      padding: 0;
      overflow: hidden;
    }

    .cm-line {
      padding: 0;
    }

    .cm-scroller {
      line-height: variables.$sticky-line-height;
      font-family: inherit;
      overflow: hidden;
    }
  }
}
</style>
